import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import clsx from 'clsx'
import {
  exportPathsFromHtmlNodes,
  findAndInsertNode,
  findAndRemoveNode,
  findAndUpdateNode,
  findQuestion,
  parseDndId,
  replaceLabelWithParentPath,
  replaceParentPathWithLabel,
} from 'components/form_builder/common/utility'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { updateHtmlNodes } from '../fields/fieldsSlice'
import MenuDropdown from './menuDropdown'
import SectionPreview from './preview'
import ResizablePanel from './resizablePanel'
import { Section, SectionHeader, SectionHeaderInput, SectionHeaderLabel } from './section'

const DragDropWrapperContext = createContext(null)

const DroppablePlaceholder = ({ text, className }) => {
  return (
    <div className={className}>
      <div className="dome-full-height dome-full-width dome-d-flex dome-justify-start dome-align-center">
        <span className="dome-p3 dome-color-lite-grey">{text}</span>
      </div>
    </div>
  )
}

// List menu dropdown component
const QuestionAsSubSectionMenuTrigger = ({ question }) => {
  const { removeNode, insertPathToParent, handleReplaceParentPathWithSubsection } = useContext(DragDropWrapperContext)

  return (
    <MenuDropdown>
      <MenuDropdown.Trigger />
      <MenuDropdown.Content>
        <MenuDropdown.Item
          text="replace parent path with label"
          onClick={() => handleReplaceParentPathWithSubsection(question)}
        />
        <MenuDropdown.NewPath onSubmit={(path) => insertPathToParent({ parent: question, path })} />
        <MenuDropdown.RemoveSection onSubmit={() => removeNode(question.id)} label="remove sub-section" />
      </MenuDropdown.Content>
    </MenuDropdown>
  )
}

const SubsectionMenuTrigger = ({ section }) => {
  const { removeNode, insertPathToParent, handleReplaceSubsectionWithParentPath } = useContext(DragDropWrapperContext)

  return (
    <MenuDropdown>
      <MenuDropdown.Trigger />
      <MenuDropdown.Content>
        <MenuDropdown.Item
          text="replace label with parent path"
          onClick={() => handleReplaceSubsectionWithParentPath(section)}
        />
        <MenuDropdown.NewPath onSubmit={(path) => insertPathToParent({ parent: section, path })} />
        <MenuDropdown.RemoveSection onSubmit={() => removeNode(section.id)} label="remove sub-section" />
      </MenuDropdown.Content>
    </MenuDropdown>
  )
}

const SectionMenuTrigger = ({ section }) => {
  const { removeNode, insertPathToParent, addNewSubSection } = useContext(DragDropWrapperContext)

  return (
    <MenuDropdown>
      <MenuDropdown.Trigger />
      <MenuDropdown.Content>
        <MenuDropdown.Item onClick={() => addNewSubSection({ parentSection: section })} text="+ new sub-section" />
        <MenuDropdown.NewPath onSubmit={(path) => insertPathToParent({ parent: section, path })} />
        <MenuDropdown.RemoveSection onSubmit={() => removeNode(section.id)} />
      </MenuDropdown.Content>
    </MenuDropdown>
  )
}
//

// Question
const QuestionItem = ({ isActive, question }) => {
  const { label, question_label, field } = question?.question_attributes || {}

  const listString = field.split('.')
  const breadCrumb = listString.filter((_, index) => index < listString.length - 1).join('.')
  const lastBreadcrumb = listString.at(-1)

  return (
    <div className={clsx('form-builder-question', { active: isActive })}>
      <div className="dome-d-flex dome-align-center dome-gap12">
        <span style={{ cursor: 'grab' }}>
          <i className="fa-light fa-grip-vertical fa-sm dome-color-med-grey form-builder-question-icon"></i>
        </span>
        <div className="dome-d-flex dome-flex-column dome-flex-1">
          <span className="dome-p2 dome-color-darkest-grey">{label}</span>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
            <span className="dome-p3 dome-color-med-grey">{question_label}</span>
            <div style={{ flexWrap: 'wrap' }} className="dome-d-flex dome-justify-end">
              <span className="dome-p3 dome-color-lite-grey">{breadCrumb ? `${breadCrumb}.` : ''}</span>
              <span className="dome-p3 dome-color-darkest-grey">{lastBreadcrumb}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const DraggableQuestion = ({ draggableId, question, index }) => {
  return (
    <Draggable draggableId={draggableId ?? `question.${question.id}`} index={index}>
      {(provided, snapshot) => (
        <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef}>
          <QuestionItem isActive={snapshot.isDragging} question={question} />
        </div>
      )}
    </Draggable>
  )
}

const DroppableSubquestion = ({ draggableId, question }) => {
  const { activeDrop } = useContext(DragDropWrapperContext)

  const subQuestionNodes = question?.nodes?.filter((node) => node.type === 'question') || []

  return (
    <Droppable isDropDisabled={!activeDrop.subsection} droppableId={`question.${question.id}`}>
      {(droppableProvided, droppableSnapshot) => {
        const isDisplayedEmptyPlaceholder = subQuestionNodes.length === 0 && !droppableSnapshot.isDraggingOver
        return (
          <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
            <div className="dome-d-flex dome-flex-column">
              {subQuestionNodes.map((node, index) => (
                <div key={node.id} style={{ paddingLeft: 12, marginLeft: 12, borderLeft: '1px solid #DBDBDB' }}>
                  <div style={{ paddingTop: 6 }}>
                    <DraggableQuestion question={node} index={index} />
                  </div>
                </div>
              ))}
              {droppableProvided.placeholder}
              {isDisplayedEmptyPlaceholder && (
                <div style={{ paddingLeft: 12, marginLeft: 12, borderLeft: '1px solid #DBDBDB' }}>
                  <div style={{ paddingTop: 6 }}>
                    <DroppablePlaceholder
                      text="drag + drop path to add to sub-section"
                      className="form-builder-empty-subsection-placeholder"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }}
    </Droppable>
  )
}
//

// Draggable Component
const DraggableQuestionAsSubsection = ({ question, index }) => {
  const { activeDrop } = useContext(DragDropWrapperContext)

  const hasAttributesField = question?.question_attributes?.field

  return (
    <Draggable draggableId={`subsection.${question.id}`} index={index}>
      {(provided, snapshot) => {
        const isDropDisabled = snapshot.isDragging || !activeDrop.subsection
        return (
          <div {...provided.draggableProps} ref={provided.innerRef}>
            <Droppable isDropDisabled={isDropDisabled} droppableId={`question-as-subsection.${question.id}`}>
              {(droppableProvided, droppableSnapshot) => {
                const isDisplayedEmptyPlaceholder = !hasAttributesField && !droppableSnapshot.isDraggingOver
                return (
                  <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                    <Section className="form-builder-sub-section">
                      <SectionHeader>
                        <SectionHeaderLabel label="subsection" dragHandleProps={provided.dragHandleProps} />
                        <QuestionAsSubSectionMenuTrigger question={question} />
                      </SectionHeader>
                      <div className="dome-d-flex dome-flex-column dome-gap3">
                        {isDisplayedEmptyPlaceholder && (
                          <DroppablePlaceholder
                            text="drag + drop path to add to sub-section"
                            className="form-builder-empty-subsection-placeholder"
                          />
                        )}
                        {hasAttributesField && (
                          <DraggableQuestion
                            index={0}
                            question={question}
                            draggableId={`question-as-subsection.${question.id}`}
                          />
                        )}
                        <DroppableSubquestion question={question} />
                      </div>
                      {droppableProvided.placeholder}
                    </Section>
                  </div>
                )
              }}
            </Droppable>
          </div>
        )
      }}
    </Draggable>
  )
}

const DraggableSubsection = ({ section, index }) => {
  const { activeDrop, updateSectionLabel } = useContext(DragDropWrapperContext)

  const { label } = section?.section_attributes || {}

  const savelSectionLabel = (value) => updateSectionLabel({ section, value })

  const questionNodes = section?.nodes?.filter((node) => node.type === 'question') || []

  return (
    <Draggable draggableId={`subsection.${section.id}`} index={index}>
      {(provided, snapshot) => {
        const isDropDisabled = snapshot.isDragging || !activeDrop.subsection
        return (
          <div {...provided.draggableProps} ref={provided.innerRef}>
            <Droppable isDropDisabled={isDropDisabled} droppableId={`subsection.${section.id}`}>
              {(droppableProvided, droppableSnapshot) => {
                const isDisplayedEmptyPlaceholder = section.nodes?.length === 0 && !droppableSnapshot.isDraggingOver
                return (
                  <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                    <Section className="form-builder-sub-section">
                      <SectionHeader>
                        <SectionHeaderInput
                          initialValue={label}
                          storeValue={savelSectionLabel}
                          dragHandleProps={provided.dragHandleProps}
                        />
                        <SubsectionMenuTrigger section={section} />
                      </SectionHeader>
                      {questionNodes.map((node, index) => (
                        <DraggableQuestion key={node.id} question={node} index={index} />
                      ))}
                      {isDisplayedEmptyPlaceholder && (
                        <DroppablePlaceholder
                          text="drag + drop path to add to sub-section"
                          className="form-builder-empty-subsection-placeholder"
                        />
                      )}
                      {droppableProvided.placeholder}
                    </Section>
                  </div>
                )
              }}
            </Droppable>
          </div>
        )
      }}
    </Draggable>
  )
}

const DraggableSection = ({ section, index }) => {
  const { activeDrop, updateSectionLabel } = useContext(DragDropWrapperContext)

  const { label } = section?.section_attributes || {}

  const savelSectionLabel = (value) => updateSectionLabel({ section, value })

  const hasAnyQuestion = section.nodes?.some((node) => node.type === 'question')

  return (
    <Draggable draggableId={`section.${section.id}`} index={index}>
      {(provided, snapshot) => {
        const isDropDisabled = snapshot.isDragging || !activeDrop.section
        return (
          <div {...provided.draggableProps} ref={provided.innerRef}>
            <Droppable droppableId={`section.${section.id}`} isDropDisabled={isDropDisabled}>
              {(droppableProvided, droppableSnapshot) => {
                const isDisplayedEmptyPlaceholder = !hasAnyQuestion && !droppableSnapshot.isDraggingOver
                return (
                  <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                    <Section className="form-builder-section">
                      <SectionHeader>
                        <SectionHeaderInput
                          initialValue={label}
                          storeValue={savelSectionLabel}
                          dragHandleProps={provided.dragHandleProps}
                        />
                        <SectionMenuTrigger section={section} />
                      </SectionHeader>
                      {section?.nodes?.map((node, index) => {
                        const type = node.type
                        const isQuestionAsSubquestion = type === 'question' && node.asSubSection
                        if (type === 'question' && !isQuestionAsSubquestion)
                          return <DraggableQuestion key={node.id} index={index} question={node} />
                        if (type === 'section')
                          return <DraggableSubsection key={node.id} index={index} section={node} />
                        if (isQuestionAsSubquestion)
                          return <DraggableQuestionAsSubsection key={node.id} index={index} question={node} />
                        return null
                      })}
                      {isDisplayedEmptyPlaceholder && (
                        <DroppablePlaceholder
                          text="drag + drop path to add to section"
                          className="form-builder-empty-section-placeholder"
                        />
                      )}
                      {droppableProvided.placeholder}
                    </Section>
                  </div>
                )
              }}
            </Droppable>
          </div>
        )
      }}
    </Draggable>
  )
}

const DroppableSectionList = () => {
  const { html_nodes } = useSelector((state) => state.fields)
  const { activeDrop } = useContext(DragDropWrapperContext)

  if (html_nodes.length === 0)
    return (
      <div style={{ width: '100%', height: 500 }}>
        <div className="dome-full-width dome-full-height dome-d-flex dome-align-center dome-justify-center">
          <span className="dome-p1 dome-color-lite-grey">start by creating a new section</span>
        </div>
      </div>
    )

  return (
    <Droppable droppableId="form-builder-sections-droppable" isDropDisabled={!activeDrop.sections}>
      {(droppableProvided, snapshot) => {
        return (
          <div
            style={{ padding: '12px 30px', flex: 1, overflow: 'auto' }}
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            <div className="dome-d-flex dome-flex-column dome-gap24">
              {html_nodes.map((section, index) => {
                if (section.type === 'section')
                  return <DraggableSection key={section.id} index={index} section={section} />
              })}
              {droppableProvided.placeholder}
            </div>
          </div>
        )
      }}
    </Droppable>
  )
}
//

const useFormSectionStructure = ({ forceDndRefreshDroppableAreas }) => {
  const dispatch = useDispatch()
  const { html_nodes, field_defs } = useSelector((state) => state.fields)

  const addNewSection = useCallback(() => {
    const newSection = {
      type: 'section',
      section_attributes: {
        label: 'section title',
      },
      nodes: [],
      id: uuidv4(),
    }
    const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
    newHtmlNodes.push(newSection)
    dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
  }, [html_nodes])

  const addNewSubSection = useCallback(
    ({ parentSection }) => {
      forceDndRefreshDroppableAreas()
      const newSection = {
        type: 'section',
        section_attributes: {
          label: 'sub-section title',
        },
        nodes: [],
        id: uuidv4(),
      }
      const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
      findAndInsertNode(newHtmlNodes, parentSection.id, newSection, parentSection?.nodes?.length || 0)
      dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
    },
    [html_nodes, forceDndRefreshDroppableAreas]
  )

  const removeNode = useCallback(
    (id) => {
      const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
      const removedNode = findAndRemoveNode(newHtmlNodes, id)
      dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
    },
    [html_nodes]
  )

  const insertPathToParent = useCallback(
    ({ parent, path }) => {
      const pathsFromHtmlNodes = exportPathsFromHtmlNodes(html_nodes)
      if (pathsFromHtmlNodes.includes(path.path)) return
      const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
      const newQuestion = {
        type: 'question',
        question_attributes: {
          field: path.path,
          label: path.field?.label,
          question_label: path.field?.question_label,
        },
        nodes: [],
        id: uuidv4(),
      }
      findAndInsertNode(newHtmlNodes, parent.id, newQuestion, parent?.nodes?.length || 0)
      dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
    },
    [html_nodes]
  )

  const updateSectionLabel = useCallback(
    ({ section, value }) => {
      const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
      const newSection = {
        ...section,
        section_attributes: {
          ...section.section_attributes,
          label: value,
        },
      }
      findAndUpdateNode(newHtmlNodes, section.id, newSection)
      dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
    },
    [html_nodes]
  )

  const handleReplaceSubsectionWithParentPath = useCallback(
    (subsection) => {
      forceDndRefreshDroppableAreas()
      const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
      replaceLabelWithParentPath(newHtmlNodes, subsection.id)
      dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
    },
    [html_nodes, forceDndRefreshDroppableAreas]
  )

  const handleReplaceParentPathWithSubsection = useCallback(
    (question) => {
      forceDndRefreshDroppableAreas()
      const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))
      replaceParentPathWithLabel(newHtmlNodes, question)
      dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
    },
    [html_nodes, forceDndRefreshDroppableAreas]
  )

  return {
    addNewSection,
    addNewSubSection,
    removeNode,
    insertPathToParent,
    updateSectionLabel,
    handleReplaceSubsectionWithParentPath,
    handleReplaceParentPathWithSubsection,
  }
}

const useFormSectionDnd = () => {
  const dispatch = useDispatch()

  const [loading, setLoading] = useState(false)
  const [activeDrop, setActiveDrop] = useState({ sections: true, section: true, question: true })

  const { html_nodes, field_defs } = useSelector((state) => state.fields)

  // This is a workaround to force react-dnd to re-register droppable areas.
  // It's used when adding a new subsection to ensure it's immediately droppable
  // by unmounting and remounting the ListSections component.
  const forceDndRefreshDroppableAreas = useCallback(() => {
    setLoading(true)
    setTimeout(() => {
      setLoading(false)
    }, 0)
  }, [])

  const handleBeforeCapture = useCallback((e) => {
    const { type } = parseDndId(e.draggableId)
    // Set active drop targets based on what is being dragged
    switch (type) {
      case 'section':
        setActiveDrop({ sections: true, section: false, subsection: false })
        break
      case 'subsection':
        setActiveDrop({ sections: false, section: true, subsection: false })
        break
      case 'question':
      case 'question-as-subsection':
      case 'unassigned-path':
        setActiveDrop({ sections: false, section: true, subsection: true })
        break
      default:
        setActiveDrop({ sections: true, section: true, subsection: true })
        break
    }
  }, [])

  const handleDropOnQuestionLabel = useCallback(({ nodes, itemType, itemId, parentId, field_defs }) => {
    if (itemType === 'question-as-subsection') {
      const question = findQuestion(nodes, itemId)
      const newQuestionNode = {
        type: 'question',
        question_attributes: { ...question.question_attributes },
        nodes: [],
        id: uuidv4(),
      }
      question.question_attributes = {}
      replaceLabelWithParentPath(nodes, parentId, newQuestionNode)
    } else if (itemType === 'question') {
      const question = findQuestion(nodes, itemId)
      findAndRemoveNode(nodes, itemId)
      replaceLabelWithParentPath(nodes, parentId, question)
    } else if (itemType === 'unassigned-path') {
      const field = field_defs[itemId]
      const newQuestionNode = {
        type: 'question',
        question_attributes: {
          field: itemId,
          label: field?.label,
          question_label: field?.question_label,
        },
        nodes: [],
        id: uuidv4(),
      }
      // This item isn't in the tree, so no remove needed
      replaceLabelWithParentPath(nodes, parentId, newQuestionNode)
    }
  }, [])

  const handleDragEnd = useCallback(
    (e) => {
      const { draggableId, destination } = e

      if (!destination?.droppableId) return

      const { type: itemType, id: itemId } = parseDndId(draggableId)
      const { type: parentType, id: parentId } = parseDndId(destination.droppableId)
      const { index: destinationIndex } = destination

      const newHtmlNodes = JSON.parse(JSON.stringify(html_nodes))

      if (itemType === 'subsection' && parentType === 'section') forceDndRefreshDroppableAreas()

      if (parentType === 'question-as-subsection') {
        handleDropOnQuestionLabel({ nodes: newHtmlNodes, itemType, itemId, parentId, field_defs })
        dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
        return
      }

      switch (itemType) {
        case 'section': {
          const movedNode = findAndRemoveNode(newHtmlNodes, itemId)
          findAndInsertNode(newHtmlNodes, null, movedNode, destinationIndex)
          break
        }
        case 'question':
        case 'subsection': {
          const movedNode = findAndRemoveNode(newHtmlNodes, itemId)
          findAndInsertNode(newHtmlNodes, parentId, movedNode, destinationIndex)
          break
        }
        case 'question-as-subsection': {
          const question = findQuestion(newHtmlNodes, itemId)
          const newQuestionNode = {
            type: 'question',
            question_attributes: { ...question.question_attributes },
            nodes: [],
            id: uuidv4(),
          }
          question.question_attributes = {}
          findAndInsertNode(newHtmlNodes, parentId, newQuestionNode, destinationIndex)
          break
        }
        case 'unassigned-path': {
          const path = field_defs[itemId]
          const newQuestionNode = {
            type: 'question',
            question_attributes: {
              field: itemId,
              label: path?.label,
              question_label: path?.question_label,
            },
            nodes: [],
            id: uuidv4(),
          }
          findAndInsertNode(newHtmlNodes, parentId, newQuestionNode, destinationIndex)
          break
        }
        default:
          console.warn(`Unknown draggable type encountered: ${itemType}`)
      }

      dispatch(updateHtmlNodes({ html_nodes: newHtmlNodes }))
    },
    [html_nodes, field_defs, handleDropOnQuestionLabel, forceDndRefreshDroppableAreas]
  )

  return {
    loading,
    activeDrop,
    handleBeforeCapture,
    handleDragEnd,
    forceDndRefreshDroppableAreas,
  }
}

export default function AssignSection() {
  const ref = useRef()

  const [position, setPosition] = useState('fixed')
  const [paddingBottom, setPaddingBottom] = useState(0)

  const { activeDrop, loading, handleBeforeCapture, handleDragEnd, forceDndRefreshDroppableAreas } = useFormSectionDnd()

  const {
    addNewSection,
    addNewSubSection,
    removeNode,
    insertPathToParent,
    updateSectionLabel,
    handleReplaceParentPathWithSubsection,
    handleReplaceSubsectionWithParentPath,
  } = useFormSectionStructure({ forceDndRefreshDroppableAreas })

  useEffect(() => {
    const handleScroll = () => {
      const resizableEle = document.getElementById('form-builder-resizeable-section')
      if (!resizableEle) return
      if (window.innerHeight - ref.current.getBoundingClientRect().bottom <= 0) {
        setPosition('fixed')
      } else {
        setPosition('absolute')
      }
      setPaddingBottom(resizableEle.clientHeight)
    }
    document.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const contextValue = useMemo(
    () => ({
      activeDrop,
      addNewSubSection,
      removeNode,
      insertPathToParent,
      updateSectionLabel,
      handleReplaceParentPathWithSubsection,
      handleReplaceSubsectionWithParentPath,
    }),
    [
      activeDrop,
      addNewSubSection,
      removeNode,
      insertPathToParent,
      updateSectionLabel,
      handleReplaceParentPathWithSubsection,
      handleReplaceSubsectionWithParentPath,
    ]
  )

  return (
    <DragDropWrapperContext.Provider value={contextValue}>
      <DragDropContext onBeforeCapture={handleBeforeCapture} onDragEnd={handleDragEnd}>
        <div ref={ref} id="form-builder-assign-section" className="dome-relative dome-d-flex dome-flex-column">
          <div style={{ paddingBottom: paddingBottom + 20 }} className="dome-d-flex">
            <div style={{ flexBasis: '65%', height: '810px', background: '#F9F9F9' }}>
              <div className="dome-d-flex dome-flex-column dome-full-height">
                <div style={{ padding: '12px 30px', background: 'white' }} className="dome-d-flex dome-justify-between">
                  <span>tools</span>
                  <div onClick={addNewSection} className="dome-d-flex dome-gap3 dome-align-center dome-cursor-pointer">
                    <i className="fa-solid fa-square-plus dome-color-aqua"></i>
                    <span className="dome-p2 dome-color-aqua">new section</span>
                  </div>
                </div>
                {!loading && <DroppableSectionList />}
              </div>
            </div>
            <SectionPreview />
          </div>
          <ResizablePanel position={position} />
        </div>
      </DragDropContext>
    </DragDropWrapperContext.Provider>
  )
}
